import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import SubjectIdentityBanner from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectIdentityBanner';
import { theme } from '@Guidelines/Theme/theme';
import describeArchivedDossier from '@UI/Pages/Beneficiaries/Shared/Helpers/describeArchivedDossier';
import BeneficiaryName from '@UI/Pages/Beneficiaries/Shared/BeneficiaryName';
import type BeneficiaryView from '@UI/Store/Beneficiaries/Views/BeneficiaryView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const archivedBannerStyle = css`
	padding: 0.875rem 1.125rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
`;

type Props = {
	beneficiary: BeneficiaryView;
	assignedUsersSummaryText: string | null;
	/** Actions drawn inside the banner, under the identity. */
	children?: React.ReactNode;
};

/**
 * The dossier's identity banner, and the "Ce dossier est archivé." notice right below it — the two
 * always sit together, so they render from a single component rather than two call sites kept in sync
 * by hand.
 *
 * `assignedUsersSummaryText` is computed once by the caller (`BeneficiaryDetailsContent`) and shared
 * with `BeneficiarySummaryAside`'s AFFECTATIONS card: the two texts can never diverge because neither
 * recomputes it.
 */
const BeneficiaryIdentityHeader: React.FC<Props> = ({ beneficiary, assignedUsersSummaryText, children }) => {
	const initials = `${beneficiary.firstName.charAt(0)}${beneficiary.lastName.charAt(0)}`.toUpperCase();

	const ageAndBirthDateText = React.useMemo(() => {
		const age = beneficiary.birthDate !== null ? beneficiary.birthDate.ageInYears() : null;
		const birthDate = beneficiary.birthDate !== null ? beneficiary.birthDate.toDisplay() : null;
		const parts = [age !== null ? `${age} ans` : null, birthDate].filter(
			(part): part is string => part !== null,
		);

		return parts.length > 0 ? parts.join(' · ') : null;
	}, [beneficiary.birthDate]);

	const followedByText = React.useMemo(() => {
		return assignedUsersSummaryText !== null ? `Suivi par ${assignedUsersSummaryText}` : null;
	}, [assignedUsersSummaryText]);

	const archivedBannerText = React.useMemo(
		() => describeArchivedDossier(beneficiary.isPurgeApproaching, beneficiary.purgeScheduledAt),
		[beneficiary.isPurgeApproaching, beneficiary.purgeScheduledAt],
	);

	return (
		<React.Fragment>
			<SubjectIdentityBanner
				initials={initials}
				seed={beneficiary.id}
				title={<BeneficiaryName firstName={beneficiary.firstName} lastName={beneficiary.lastName} />}
				tags={
					<ConditionalLoader data={beneficiary.isArchived}>
						<Tag value="Archivé" severity="secondary" rounded />
					</ConditionalLoader>
				}
				metadata={
					<React.Fragment>
						<ConditionalLoader data={beneficiary.birthNameMention}>
							{(birthNameMention) => <li>{birthNameMention}</li>}
						</ConditionalLoader>
						<ConditionalLoader data={ageAndBirthDateText}>
							{(text) => <li>{text}</li>}
						</ConditionalLoader>
						<ConditionalLoader data={beneficiary.homeAddress}>
							{(homeAddress) => <li>{homeAddress}</li>}
						</ConditionalLoader>
						<ConditionalLoader data={followedByText}>
							{(text) => <li>{text}</li>}
						</ConditionalLoader>
					</React.Fragment>
				}
			>
				{children}
			</SubjectIdentityBanner>

			{beneficiary.isArchived && <p css={archivedBannerStyle}>{archivedBannerText}</p>}
		</React.Fragment>
	);
};

export default BeneficiaryIdentityHeader;
